'use client';

import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { HUB_EVENTS, pushDataLayer } from '@/lib/analytics';
import { SHORT_LOCALES } from '@/lib/config';
import type { HubContent } from '@/lib/content/hub';

type NavItem = { href: string; id: string; label: string };

function LocaleSwitch({ locale, label }: { locale: Locale; label: string }) {
  const router = useRouter();

  return (
    <div className="flex items-center border border-cream/30" role="group" aria-label={label}>
      {SHORT_LOCALES.map((code) => {
        const active = code === locale;
        return (
          <button
            key={code}
            type="button"
            aria-pressed={active}
            onClick={() => {
              if (active) return;
              const hash = typeof window !== 'undefined' ? window.location.hash : '';
              router.push(`/hub/${code}${hash}`);
            }}
            className={`hub-label-sm flex min-h-[40px] min-w-[40px] items-center justify-center px-2.5 transition-colors ${
              active ? 'bg-yamal text-coal' : 'text-cream/60 hover:text-cream'
            }`}
          >
            {code}
          </button>
        );
      })}
    </div>
  );
}

export default function Header({ content, locale }: { content: HubContent; locale: Locale }) {
  const nav: NavItem[] = [
    { href: '#map', id: 'map', label: content.header.nav.map },
    { href: '#win', id: 'win', label: content.header.nav.win },
    { href: '#alerts', id: 'alerts', label: content.header.nav.alerts },
    { href: '#shop', id: 'shop', label: content.header.nav.shop },
  ];

  const headerRef = useRef<HTMLElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState<string>('top');

  // Keep --header-h in sync with the rendered header height.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const sync = () =>
      document.documentElement.style.setProperty('--header-h', `${el.offsetHeight}px`);
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll + close on Escape while the mobile menu is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Scroll-spy: mark the section currently in view.
  useEffect(() => {
    const ids = ['top', ...nav.map((item) => item.id)];
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActiveId(visible.target.id);
      },
      { rootMargin: '-40% 0px -55% 0px', threshold: [0, 0.25, 0.5, 1] },
    );
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
    // nav is derived from static content; its ids never change at runtime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNavClick = useCallback((item: NavItem) => {
    setOpen(false);
    pushDataLayer(HUB_EVENTS.NAV_CLICK, { section: item.id });
  }, []);

  return (
    <header
      ref={headerRef}
      className={`fixed inset-x-0 top-0 z-[1000] border-b border-cream/12 bg-coal/95 pt-[env(safe-area-inset-top)] backdrop-blur-md transition-colors duration-300 lg:border-transparent ${
        scrolled || open ?
          'lg:border-cream/12 lg:bg-coal/95'
        : 'lg:bg-transparent lg:backdrop-blur-none'
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-5 py-3.5 sm:px-8">
        <nav className="hidden items-center gap-7 md:flex" aria-label="Primary">
          {nav.map((item) => (
            <a
              key={item.id}
              href={item.href}
              aria-current={activeId === item.id ? 'true' : undefined}
              onClick={() => handleNavClick(item)}
              className="hub-label text-cream/80 transition-colors hover:text-yamal aria-[current]:text-yamal"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden md:block">
            <LocaleSwitch locale={locale} label={content.header.localeSwitch} />
          </div>
          <a
            href="#win"
            onClick={() => handleNavClick({ href: '#win', id: 'win', label: '' })}
            className="hub-display hidden items-center bg-yamal px-4 py-3 text-[17px] tracking-[0.02em] text-coal transition-transform hover:-translate-y-0.5 active:translate-y-0 sm:inline-flex sm:px-5 sm:text-[19px]"
          >
            {content.giveaway.cta}
          </a>
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
            aria-controls="hub-mobile-nav"
            aria-label={open ? content.header.closeMenu : content.header.openMenu}
            className="grid size-11 place-items-center border border-cream/25 text-cream active:bg-cream/10 md:hidden"
          >
            <span aria-hidden="true" className="text-xl leading-none">
              {open ? '✕' : '☰'}
            </span>
          </button>
        </div>
      </div>

      {open ?
        <nav
          id="hub-mobile-nav"
          aria-label="Primary"
          className="border-t border-cream/12 md:hidden"
        >
          {nav.map((item) => (
            <a
              key={item.id}
              href={item.href}
              aria-current={activeId === item.id ? 'true' : undefined}
              onClick={() => handleNavClick(item)}
              className="hub-label-lg block border-b border-cream/8 px-5 py-4 text-cream/85 active:bg-cream/10"
            >
              {item.label}
            </a>
          ))}
          <div className="flex items-center justify-between px-5 py-4">
            <span className="hub-label-sm text-cream/60">{content.header.localeSwitch}</span>
            <LocaleSwitch locale={locale} label={content.header.localeSwitch} />
          </div>
        </nav>
      : null}
    </header>
  );
}
